import _ from 'lodash';
import { CONFIG, type supportedCountries } from '../../../config';
import { FeaturePermission } from '../../../constants/feature.permission.helper';
import { ContextEnum } from '../../../enuns/context.enum';
import { CountryEnum } from '../../../enuns/country.enum';
import api from '../../api';
import environment from '../../environment';
import Leaflet from '../../layers/Leaflet';
import Workspace from '../../workspaces';
import { ControlType, Item } from '../../workspaces/item';
import filters from '../filters';
import translate from '../tools/translate';

const layers = () => require('../../layers').default;
const sidebar = () => require('../sidebar').default;

const bounds: { country: supportedCountries, bounds: number[][] }[] = [
    {
        country: CountryEnum.Brasil,
        zoom: 5,
        bounds: [
            [-33.97980872872456, -84.0234375],
            [5.878332109674327, -12.216796875]
        ]
    },
    {
        country: CountryEnum.Argentina,
        zoom: 4.8,
        bounds: [
            [-55.0556, -73.5708],
            [-21.7808, -53.6372]
        ]
    },
    {
        country: CountryEnum.Equador,
        zoom: 7.5,
        bounds: [
            [1.643115, -81.593178],
            [-5.162143, -74.796064]
        ]
    },
    {
        country: CountryEnum.Colombia,
        zoom: 6,
        bounds: [
            [-4.22941, -81.7356],
            [13.3947, -66.8472]
        ]
    },
    {
        country: CountryEnum.Peru,
        zoom: 6,
        bounds: [
            [-18.3509, -81.3282],
            [-0.038606, -68.6523]
        ]
    },
    {
        country: CountryEnum.Chile,
        zoom: 4.5,
        bounds: [
            [-57.04515142976343, -61.550482396470464],
            [-14.731285239562233, -79.61595730059543]
        ]
    },
    {
        country: CountryEnum.RepublicaDominicana,
        zoom: 8.5,
        bounds: [
            [20.131558432762183, -72.32280741222101],
            [17.33836798491249, -67.60417967206057]
        ]
    },
    {
        country: CountryEnum.Guatemala,
        zoom: 7,
        bounds: [
            [17.895754524819598, -91.29598524982755],
            [12.946331452804978, -88.29671776282555]
        ]
    },
    {
        country: CountryEnum.Uruguai,
        zoom: 6,
        bounds: [
            [-30.0859, -58.4501],
            [-35.6501, -53.0501]
        ]
    },
    {
        country: CountryEnum.ElSalvador,
        zoom: 8.5,
        bounds: [
            [14.4509, -90.1347],
            [13.1558, -87.6908]
        ]
    },
    {
        country: CountryEnum.Panama,
        zoom: 7.5,
        bounds: [
            [9.6475, -83.0523],
            [7.2035, -77.1585]
        ]
    },
];

const map = (() => {
    const onSelect = (currentMap) => {
        sidebar().setControls(currentMap._id);

        $('.layer-indicator').html(currentMap._id + 1);

        $('.map-active').each((i, e) => {
            $(e).removeClass('map-active');
        });

        if (map.get().size() > 1) { $(currentMap.getContainer()).addClass('map-active'); }

        _.forEach(layers().getAll(currentMap._id), (layer, name) => {
            try {
                if (layer instanceof Leaflet.GeoJSONLayer || layer instanceof Leaflet.D3Layer) {
                    if (layer.visible()) { layer.reload(); }
                }
            } catch (err) {
                console.log(err);
            }
        });

        filters.reloadFilters();
    };

    const mapLayer = new Leaflet.Maps(0, {}, onSelect);


    // MapBox Key
    if (!L.mapbox) { L.mapbox = {}; }

    const context = new Leaflet.Directions.Google({
        endMarkerIcon: L.icon({
            iconUrl: `${environment.get('IMAGES_HOST')}/pin_directions_end_alt.png`,
            iconSize: [20, 23], // size of the icon
            iconAnchor: [10, 23] // marker's location
        }),
        startMarkerIcon: L.icon({
            iconUrl: `${environment.get('IMAGES_HOST')}/pin_directions_start_alt.png`,
            iconSize: [20, 23], // size of the icon
            iconAnchor: [10, 23] // marker's location
        }),
        map: mapLayer
    });

    const zoomControl = {
        normal: L.control.zoom({ position: 'topright' }),
        visible: 'normal'
    };

    const mapboxKey = CONFIG.MAPBOX_KEY;
    const cartoKey = CONFIG.CARTO_KEY;

    const _getMapTiles = () => {
        const output = {
            default: L.tileLayer(`https://api.mapbox.com/styles/v1/neocomservice/ckfxzpjpv00zg19lk6129xhf7/tiles/{z}/{x}/{y}?access_token=${mapboxKey}`, {
                tileSize: 512,
                zoomOffset: -1,
                attribution: '© <a href="https://apps.mapbox.com/feedback/">Mapbox</a> © <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            }),
            roads: L.tileLayer(`https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png?key=${cartoKey}`, {
                attribution: '&copy; OpenStreetMap contributors & CartoDB',
                subdomains: 'abcd',
                maxZoom: 19
            }),
            sattelite: L.tileLayer(`https://api.mapbox.com/styles/v1/neocomservice/cmdhqxls4009001qwfq4a4v51/tiles/{z}/{x}/{y}?access_token=${mapboxKey}`, {
                tileSize: 512,
                zoomOffset: -1,
                attribution: '© <a href="https://apps.mapbox.com/feedback/">Mapbox</a> © <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            }),
            cartodb: L.tileLayer(`https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png?key=${cartoKey}`, {
                attribution: '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="http://carto.com/attributions">Carto</a>',
                subdomains: 'abcd',
                maxZoom: 19
            }),
            openstreet: L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                attribution: '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>',
                maxZoom: 19
            }),
            grayscale: L.tileLayer.grayscale('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
                attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
                maxZoom: 19
            }),
            nomap: L.tileLayer('', {
                attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
                maxZoom: 19
            })
        };

        return output;
    };


    const _contextMenu = () => {
        const options = [
            { text: translate('Action.ZoomPlus'), callback: context.zoomIn, icon: `${environment.get('IMAGES_HOST')}/zoom-in.png` },
            { text: translate('Action.ZoomLess'), callback: context.zoomOut, icon: `${environment.get('IMAGES_HOST')}/zoom-out.png` }
        ];

        if (!api.authorization.isAuthorized) { return options; }

        // context-get-location
        options.push('-');
        options.push({ text: translate('Action.Coordinates'), callback: context.showCoordinates });

        if (api.authorization.isAuthorized(FeaturePermission.MAP_POINT_INTEREST.create)) {
            options.push('-');
            options.push({
                text: translate('Action.CreatePoint'),
                callback: context.createPOI,
                icon: `${environment.get('IMAGES_HOST')}/pois.png`
            });
        }

        // context-street-view
        options.push('-');
        options.push({
            text: 'Street view',
            callback: context.streetView,
            icon: `${environment.get('IMAGES_HOST')}/street-view.png`
        });

        // context-routes
        options.push('-');
        options.push({ text: translate('Action.OriginRoute'), callback: context.setStartPosition });
        options.push({ text: translate('Action.EndRoute'), callback: context.setEndPosition });
        options.push({ text: translate('Action.RemoveRoutes'), callback: context.removePoints });

        if (api.authorization.isAuthorized('context-get-screen')) {
            const button = $('#trigger-map-capture');
            button.show();
            button.on('click', (ev) => {
                ev.preventDefault();
                return context.screenshot();
            });

            options.push('-');
            options.push({ text: translate('Action.Screenshot'), callback: context.screenshot });
        }

        return options;
    };

    return {

        getContext() {
            return context;
        },

        getThumbImageSize() {
            return '360x194';
        },

        create(container) {
            const tiles = _getMapTiles();
            const options = {
                renderer: L.canvas(),
                center: new L.LatLng(-15.0, -53.0),
                zoom: bounds.find(x => x.country === window.NeoGIG.country).zoom || 5,
                minZoom: 3,
                zoomControl: false,
                zoomDelta: 0.25,
                zoomSnap: 0,
                doubleClickZoom: false,
                layers: [tiles.default],
                attributionControl: false,
                maxBounds: bounds.find(x => x.country === window.NeoGIG.country).bounds,
                contextmenu: true,
                contextmenuWidth: 140,
                contextmenuItems: _contextMenu(),
                preferCanvas: true
            };

            mapLayer.add(container, options);

            const worldMap = mapLayer.get();
            worldMap._tiles = tiles;

            L.control.attribution({
                prefix: `NeoGIG - Versão ${environment.get('VERSION')}`,
                position: 'bottomright'
            }).addTo(worldMap);
            // scale on map
            L.control.scale({
                position: 'bottomright',
                imperial: false
            }).addTo(worldMap);
            // zoom slide control
            zoomControl.normal.addTo(worldMap);

            this.on('dragend', () => {
                Workspace.addAction(new Item("mapView", {
                    latlng: this.getCenter(),
                    zoom: this.getZoom()
                }, ControlType.MapView));
            });

            this.on('zoomend', () => {
                Workspace.addAction(new Item("mapView", {
                    latlng: this.getCenter(),
                    zoom: this.getZoom()
                }, ControlType.MapView));
            });
        },

        get(id) {
            if (mapLayer instanceof Leaflet.Maps && _.isNumber(id)) {
                return mapLayer.get(id);
            }

            return mapLayer;
        },

        // END: get = ->
        getCurrent() {
            if (mapLayer instanceof Leaflet.Maps) { return mapLayer.get(); }
            return this.get();
        },

        // END: getCurrent = ->
        getZoom() {
            return mapLayer.getZoom();
        },

        // END: getZoom = ->
        getCenter() {
            return mapLayer.getCenter();
        },

        // END: getCenter = ->
        setZoom(value) {
            return mapLayer.setZoom(value);
        },

        // END: setZoom = (value) ->
        setView(latlng, zoom) {
            return mapLayer.setView(latlng, zoom);
        },

        selectMap(id) {
            return mapLayer.set(id);
        },

        getCurrentMapId() {
            return this.getCurrent()._id;
        },

        // END: setView = (value) ->
        panTo(latlng) {
            return mapLayer.panTo(latlng);
        },

        // END: panTo = (latlng) ->
        setLayer(layerName) {
            api.general.log.action(ContextEnum.PanelSuperior, 'Alterar Base Map', JSON.stringify({ name: layerName }));
            const currentMap = mapLayer.get();
            _.forEach(currentMap._tiles, (layer, name) => {
                if (currentMap.hasLayer(layer) && name !== layerName) {
                    currentMap.removeLayer(layer);
                } else if (name === layerName) {
                    if (!currentMap.hasLayer(layer)) {
                        currentMap.addLayer(layer);
                    }
                }
            });
        },

        // END: setLayer = (layerName) ->
        refresh() {
            if (mapLayer) {
                return mapLayer.invalidateSize();
            }
        },

        // END: refresh = ->
        hasLayer(layer) {
            if (_.isString(layer)) {
                ({ layer } = layers().get(layer));
            }

            return mapLayer.hasLayer(layer);
        },

        // END: hasLayer = (layer) ->
        addLayer(layer) {
            return mapLayer.addLayer(layer);
        },

        // END: addLayer = (layer) ->
        addControl(control) {
            return mapLayer.addControl(control);
        },

        // END: addControl = (control) ->
        removeLayer(layer) {
            return mapLayer.removeLayer(layer);
        },

        // END: removeLayer = (layer) ->
        removeControl(control) {
            return mapLayer.removeControl(control);
        },

        // END: interaction = (value) ->
        remove(id = 1) {
            mapLayer.remove(id);
        },

        // remove = (dual = false) ->
        on(event, callback) {
            return mapLayer.on(event, callback);
        },

        // END: addEventListener = (event, callback) ->
        off(event, callback) {
            return mapLayer.off(event, callback);
        },

        // END: removeEventListener = (event, callback) ->
        once(event, callback) {
            return mapLayer.once(event, callback);
        },

        // END: addOneTimeEventListener = (event, callback) ->
        fire(event) {
            return mapLayer.fire(event);
        },

        getCurrentBbox() {
            const bounds = this.getCurrent().getBounds();
            const sw = bounds.getSouthWest();
            const ne = bounds.getNorthEast();
            return `LINESTRING(${ne.lng} ${ne.lat}, ${sw.lng} ${sw.lat})`;
        }
    };
})();

export default map;



// WEBPACK FOOTER //
// ./src/scripts/core/map/index.js